/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT SWATCH (cross-product size selector)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Swaps the whole main product section in place when a size swatch pointing at another product is clicked, so
 * that switching size behaves like switching a variant instead of reloading the page.
 *
 * This is not something the theme already does. <variant-picker> only ever re-renders the product it is bound to,
 * and <product-rerender> matches the incoming fragment by id (theme.js:5211) - those ids embed the product id, so
 * they never match across two different products. Focal's own cross-product option values are plain links that
 * navigate away (snippets/product-form.liquid:134). Replacing the section sidesteps the id problem entirely and
 * lets every custom element inside re-initialise through its own connectedCallback.
 *
 * Markup is rendered by snippets/product-swatch.liquid.
 */
(() => {
  const sectionSelector = '.shopify-section--main-product',
        htmlCache = new Map();

  let pendingUrl = null;

  /**
   * Only the product section is fetched, not the whole page: on this store that is ~171kb against ~811kb. A linked
   * product sitting on another template renders under a different section id, so fall back to its full page when the
   * section render comes back without one.
   */
  const loadSection = (url, sectionId) => {
    const cacheKey = `${url}::${sectionId}`;

    if (!htmlCache.has(cacheKey)) {
      const request = fetch(`${url}?section_id=${encodeURIComponent(sectionId)}`)
        .then((response) => response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
        .then((html) => html.includes('shopify-section--main-product') ? html : fetch(url).then((response) => response.text()));

      request.catch(() => htmlCache.delete(cacheKey));
      htmlCache.set(cacheKey, request);
    }

    return htmlCache.get(cacheKey);
  };

  class ProductSwatch extends HTMLElement {
    connectedCallback() {
      this.section = this.closest(sectionSelector);
      this.sectionId = this.section?.id.replace('shopify-section-', '');

      if (!this.sectionId) {
        return;
      }

      this.querySelectorAll('a[data-product-swatch]').forEach((link) => {
        const prefetch = () => loadSection(link.href, this.sectionId).catch(() => {});

        link.addEventListener('click', this._onClick.bind(this));
        link.addEventListener('pointerenter', prefetch);
        link.addEventListener('focus', prefetch);
      });
    }

    async _onClick(event) {
      const link = event.currentTarget;

      // Let the browser handle anything that is not a plain left click, and ignore the product already shown
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('aria-current')) {
        return;
      }

      event.preventDefault();

      pendingUrl = link.href;
      this.classList.add('is-loading');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const html = await loadSection(link.href, this.sectionId);

        // A later click won the race while this one was in flight
        if (pendingUrl !== link.href) {
          return;
        }

        const nextSection = new DOMParser().parseFromString(html, 'text/html').querySelector(sectionSelector);

        if (!nextSection) {
          throw new Error('no main product section in the response');
        }

        this.section.replaceWith(nextSection);
        window.history.pushState({ productSwatch: true }, '', link.href);

        window.Shopify?.PaymentButton?.init();
        document.dispatchEvent(new CustomEvent('product:changed', { bubbles: true, detail: { productUrl: link.href } }));
      } catch (error) {
        console.warn('[product-swatch] falling back to a full page load:', error);
        window.location.href = link.href;
      } finally {
        this.classList.remove('is-loading');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }

  // The swapped-out markup is gone, so let the browser rebuild the page it is navigating back to
  window.addEventListener('popstate', (event) => {
    if (event.state?.productSwatch || document.querySelector('product-swatch')) {
      window.location.reload();
    }
  });

  if (!window.customElements.get('product-swatch')) {
    window.customElements.define('product-swatch', ProductSwatch);
  }
})();
/**
 * ------------------------------------------------------------------------------------------------------------
 * SAN PHILOSOPHY (auto-rotating ring of steps)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Advances the <tabs-nav> inside sections/san-philosophy.liquid on a timer. Everything else about that section -
 * selecting a step, crossfading the panels, keeping the selection exclusive, the theme editor picking a block -
 * is <tabs-nav> itself (theme.js:2398); this only calls its selectButton() when the timer fires.
 *
 * Nothing in the theme rotates on its own except <announcement-bar> (theme.js:3314), and its player is bound to
 * <announcement-bar-item> children and writes --announcement-bar-height onto the document, so it cannot drive a
 * tabs-nav. <slide-show> and <flickity-carousel> autoplay, but they lay their cells out horizontally and take the
 * markup over, which a ring of absolutely placed points cannot use.
 */
(() => {
  class SanPhilosophy extends HTMLElement {
    isVisible = false;
    isInteracting = false;

    async connectedCallback() {
      await window.customElements.whenDefined('tabs-nav');

      this.tabsNav = this.querySelector('tabs-nav');

      // One step has nowhere to rotate to, and a visitor who asked for reduced motion moves through them by hand
      if (!(this.tabsNav?.buttons?.length > 1) || this.interval <= 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this.addEventListener('pointerenter', this._hold);
      this.addEventListener('pointerleave', this._release);
      this.addEventListener('focusin', this._hold);
      this.addEventListener('focusout', this._release);

      // A click restarts the countdown, so a step the visitor picked gets its full turn
      this.tabsNav.addEventListener('tabs-nav:changed', this._restart);

      // An off-screen section has nothing to animate, so the timer only runs while this one is in view
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          this._restart();
        });
      });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      clearInterval(this._timer);
      this.observer?.disconnect();
    }

    get interval() {
      return parseInt(this.getAttribute('interval')) || 0;
    }

    next() {
      const { buttons, selectedTabIndex } = this.tabsNav;

      this.tabsNav.selectButton(buttons[(selectedTabIndex + 1) % buttons.length]);
    }

    _hold = () => {
      this.isInteracting = true;
      this._restart();
    };

    _release = () => {
      this.isInteracting = false;
      this._restart();
    };

    // The only place the timer is set: it runs when the section is on screen and nobody is pointing at it
    _restart = () => {
      clearInterval(this._timer);

      if (this.isVisible && !this.isInteracting) {
        this._timer = setInterval(this.next.bind(this), this.interval);
      }
    };
  }

  if (!window.customElements.get('san-philosophy')) {
    window.customElements.define('san-philosophy', SanPhilosophy);
  }
})();
/**
 * ------------------------------------------------------------------------------------------------------------
 * MARQUEE LOGOS (endless logo strip)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Fills the track in sections/marquee-logo.liquid with as many copies of the logo row as the viewport needs, and
 * hands theme.css the two numbers its animation cannot work out on its own: how many copies the track holds (the
 * animation travels 100% / that, which is one copy exactly, so the loop closes on itself) and how long one copy
 * takes at the speed the merchant picked. Both are worked out again once the logos decode and on every resize.
 *
 * Nothing in the theme loops without a seam: <logo-list> (theme.js:4423) is a grid or a prev/next scroller, and
 * <flickity-carousel>, <slide-show> and <announcement-bar> all step from one item to the next and rewind to the
 * first when they run out - which is the jump this section exists to avoid.
 */
(() => {
  class MarqueeLogos extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.marquee-logos__track');
      this.group = this.track?.firstElementChild;

      // With no row to repeat there is nothing to loop, and a visitor who asked for reduced motion keeps the one
      // row the section rendered, which theme.css leaves still and scrollable by hand
      if (!this.group || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this._fill();

      // The row measures differently once the logos decode, and again at every breakpoint, so the copy count and
      // the duration follow the element instead of being measured once at load
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this._frame);
        this._frame = requestAnimationFrame(() => this._fill());
      });

      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group);

      // An off-screen strip has nothing to show, and a paused animation costs the compositor nothing
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => this.toggleAttribute('visible', entry.isIntersecting));
      }, { rootMargin: '200px 0px' });

      this.intersectionObserver.observe(this);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._frame);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
    }

    // Pixels per second, so a row of three logos travels at the same pace as a row of twelve
    get speed() {
      return parseFloat(this.getAttribute('speed')) || 50;
    }

    _fill() {
      const groupWidth = this.group.getBoundingClientRect().width;

      if (groupWidth === 0) {
        return;
      }

      // Two copies are the fewest a loop can use; past that, enough of them to cover the strip twice over, so the
      // tail of the last copy is still off screen at the moment the first one comes back round
      const groups = Math.max(2, Math.ceil((this.clientWidth * 2) / groupWidth));

      while (this.track.children.length < groups) {
        const copy = this.group.cloneNode(true);

        // The logos are announced once and picked in the editor once; the copies only fill the strip
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('[data-shopify-editor-block]').forEach((item) => item.removeAttribute('data-shopify-editor-block'));

        this.track.append(copy);
      }

      while (this.track.children.length > groups) {
        this.track.lastElementChild.remove();
      }

      this.style.setProperty('--marquee-groups', groups);

      // A "duration" attribute (seconds per copy) wins over "speed" for sections whose setting is a cycle time
      const duration = parseFloat(this.getAttribute('duration')) || groupWidth / this.speed;

      this.style.setProperty('--marquee-duration', `${duration.toFixed(2)}s`);
    }
  }

  if (!window.customElements.get('marquee-logos')) {
    window.customElements.define('marquee-logos', MarqueeLogos);
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * WISHLIST BUTTON (Swym Wishlist Plus)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The heart on the product images (snippets/product-media.liquid) and on collection page cards
 * (snippets/product-item.liquid). It stays hidden until Swym has loaded, then shows whether the variant is already
 * saved and adds or removes it on click through Swym's own API.
 *
 * Nothing in the theme talks to a wishlist app, hence this element. It carries no "swym-*" classes on purpose, so
 * Swym does not bind its own handler to it and there is no need to fight it for the click. On the product page a
 * variant change re-renders <product-media>, so a fresh element checks its own state in connectedCallback. On a
 * card nothing re-renders: product-item.js rewrites data-variant-id on a swatch change, which re-checks the state.
 */
(() => {
  // Swym runs window.SwymCallbacks once it has loaded; if it already has, run the callback right away
  const whenSwymReady = (callback) => {
    if (window._swat) {
      return callback(window._swat);
    }

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  };

  class WishlistButton extends HTMLElement {
    static get observedAttributes() {
      return ['data-variant-id'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== null && oldValue !== newValue) {
        this._setAdded(false);
        this._refreshState();
      }
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', this._onClick.bind(this));

      whenSwymReady((swat) => {
        this.swat = swat;
        this.setAttribute('ready', '');
        this._refreshState();
      });
    }

    get _product() {
      return {
        epi: Number(this.dataset.variantId),
        empi: Number(this.dataset.productId),
        du: this.dataset.productUrl
      };
    }

    _setAdded(isAdded) {
      this.classList.toggle('is-added', isAdded);
      this.button?.setAttribute('aria-pressed', String(isAdded));
    }

    _refreshState() {
      if (!this.swat?.wishlistContainsProduct) {
        return;
      }

      this.swat.wishlistContainsProduct(Number(this.dataset.productId), Number(this.dataset.variantId), () => this._setAdded(true), () => this._setAdded(false));
    }

    _onClick() {
      if (!this.swat) {
        return;
      }

      if (this.classList.contains('is-added')) {
        this.swat.removeFromWishList(this._product, () => this._setAdded(false));
      } else {
        this.swat.addToWishList(this._product, () => this._setAdded(true));
      }
    }
  }

  if (!window.customElements.get('wishlist-button')) {
    window.customElements.define('wishlist-button', WishlistButton);
  }

  // Adding to cart from Swym's own wishlist page or popup: refresh the theme's cart count and drawer
  whenSwymReady((swat) => {
    const refreshCart = () => document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    swat.evtLayer?.addEventListener(swat.JSEvents.UIAddedToCart, refreshCart);
    swat.evtLayer?.addEventListener(swat.JSEvents.UIAddedAllToCart, refreshCart);
  });
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * ESTIMATED DELIVERY DATE (<sandeep-edd>)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Pincode checker for the "sandeep_edd" product block (snippets/sandeep-edd.liquid). The pincode JSON is fetched
 * once per page and shared by every instance (the quick buy renders the product form more than once). The button
 * is the theme's loader-button, so setting aria-busy gives the theme's own spinner.
 *
 * Data format: { "110001": { "days": 1 }, "560001": { "days": 3 }, "999999": { "available": false } }
 */
(() => {
  const resultStates = ['express', 'standard', 'unavail', 'error'];

  let dataRequest = null;

  // A failed request is dropped from the cache so "Try again" really retries
  const loadData = (url) => {
    if (!dataRequest) {
      dataRequest = fetch(url).then((response) => response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`)));
      dataRequest.catch(() => dataRequest = null);
    }

    return dataRequest;
  };

  // Dispatch happens the same day before the cutoff hour, the next day after it
  const getDeliveryLabel = (days, cutoffHour) => {
    const now = new Date(),
          date = new Date(now);

    date.setDate(date.getDate() + days + (now.getHours() >= cutoffHour ? 1 : 0));

    if (days === 1 && now.getHours() < cutoffHour) {
      return 'Tomorrow';
    }

    return `${date.toLocaleDateString('en-IN', { weekday: 'long' })}, ${date.getDate()} ${date.toLocaleDateString('en-IN', { month: 'long' })}`;
  };

  class SandeepEdd extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.edd__input');
      this.cutoffHour = parseInt(this.dataset.cutoff, 10) || 15;
      this.abortController = new AbortController();

      const signal = this.abortController.signal;

      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
        this.setState(this.input.value.length === 6 ? 'filled' : 'default');
      }, { signal });

      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault(); // The block can sit inside the product form: never submit it
          this.check();
        }
      }, { signal });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-edd-retry]')) {
          this.check();
        } else if (event.target.closest('.edd__button')) {
          resultStates.includes(this.dataset.state) ? this.reset() : this.check();
        }
      }, { signal });

      // Warm the data up before the customer gets to the field
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.observer.disconnect();
          loadData(this.dataset.url).catch(() => {});
        }
      }, { rootMargin: '200px' });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.abortController.abort();
      this.observer.disconnect();
    }

    // The button is queried every time: loader-button rebuilds its content when it upgrades, which can happen after us
    get button() {
      return this.querySelector('.edd__button');
    }

    setState(state) {
      const hasResult = resultStates.includes(state),
            button = this.button;

      this.dataset.state = state;
      this.input.disabled = hasResult || state === 'loading';

      button.disabled = state === 'default' || state === 'loading';
      button.classList.toggle('button--primary', !hasResult);
      button.classList.toggle('button--outline', hasResult);
      state === 'loading' ? button.setAttribute('aria-busy', 'true') : button.removeAttribute('aria-busy'); // loader-button only reacts to "true"

      this.querySelectorAll('[data-edd-state]').forEach((element) => element.hidden = element.dataset.eddState !== state);
      this.querySelectorAll('[data-edd-label]').forEach((element) => element.hidden = (element.dataset.eddLabel === 'change') !== hasResult);
      this.querySelector('[data-edd-teaser]')?.toggleAttribute('hidden', state !== 'default' && state !== 'filled');
    }

    async check() {
      const pincode = this.input.value;

      if (pincode.length !== 6 || this.dataset.state === 'loading') {
        return;
      }

      this.setState('loading');

      let info;

      try {
        info = (await loadData(this.dataset.url))[pincode];
      } catch (error) {
        console.warn('[sandeep-edd] Unable to load the pincode data', error);
        return this.setState('error');
      }

      if (!info || info.available === false) {
        return this.setState('unavail');
      }

      const days = parseInt(info.days, 10),
            state = days === 1 ? 'express' : 'standard',
            label = Number.isNaN(days) ? String(info.days ?? '') : getDeliveryLabel(days, this.cutoffHour);

      this.querySelector(`[data-edd-state="${state}"] [data-edd-date]`).textContent = label;
      this.setState(state);
    }

    reset() {
      this.input.value = '';
      this.setState('default');
      this.input.focus();
    }
  }

  if (!window.customElements.get('sandeep-edd')) {
    window.customElements.define('sandeep-edd', SandeepEdd);
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * COLLECTION FILTER PANEL OFFSET (sections/main-collection.liquid + main-search.liquid, "Collapsible panel" filters)
 * ------------------------------------------------------------------------------------------------------------
 *
 * The filter panel is a <safe-sticky> (theme.js), which sticks at the header offset plus its "offset" attribute.
 * In this layout the sticky .product-facet__toolbar sits above it too, and its height depends on the font and on
 * the active filter tags it holds, so it is measured rather than hardcoded. <product-facet> only swaps the
 * toolbar's innerHTML on a filter change, so the observed element survives, and <safe-sticky> reads the
 * attribute on every scroll.
 */
(() => {
  const gap = 24;

  const observe = (root) => {
    root.querySelectorAll('product-facet.product-facet--toggle').forEach((facet) => {
      const toolbar = facet.querySelector('.product-facet__toolbar'),
            panel = facet.querySelector('safe-sticky.product-facet__aside-inner');

      if (!toolbar || !panel || !window.ResizeObserver) {
        return;
      }

      new ResizeObserver(() => panel.setAttribute('offset', toolbar.offsetHeight + gap)).observe(toolbar);
    });
  };

  observe(document);
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * FOOTER NEWSLETTER PHONE + CONSENT (sections/footer.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Shopify's customer form only stores the email, so the phone number and SMS/RCS/WhatsApp consent are
 * appended to the "contact[tags]" input right before submit. They show up as tags on the customer
 * (e.g. "newsletter, phone:+919999999999, sms-whatsapp-consent"). The listener is delegated so it keeps
 * working when the footer is re-rendered in the theme editor.
 */
document.addEventListener('submit', (event) => {
  const form = event.target;

  if (form.id !== 'footer-newsletter') {
    return;
  }

  const tagsInput = form.querySelector('[data-newsletter-tags]'),
        phoneInput = form.querySelector('[data-newsletter-phone]'),
        consentInput = form.querySelector('[data-newsletter-consent]');

  if (!tagsInput) {
    return;
  }

  const tags = ['newsletter'],
        phone = phoneInput ? phoneInput.value.replace(/[^0-9+]/g, '') : '';

  if (phone) {
    tags.push(`phone:${phone}`);
  }

  if (consentInput && consentInput.checked) {
    tags.push('sms-whatsapp-consent');
  }

  tagsInput.value = tags.join(', ');
});

/**
 * ------------------------------------------------------------------------------------------------------------
 * PINNED PRODUCT (ad landing on a collection)
 * ------------------------------------------------------------------------------------------------------------
 *
 * An ad links to its collection with ?pin=<product-handle>, and that product becomes the first card of the grid.
 * Several comma-separated handles (?pin=handle-a,handle-b,handle-c) become the first cards in that order, and a
 * handle that matches no product is skipped. Without a pin, or on a filtered or later page, the collection is left
 * exactly as rendered.
 *
 * A product already on the first page is simply moved, so the common case costs no request. Otherwise its card
 * is fetched as sections/pinned-product.liquid, which renders the same product-item snippet, and given the image
 * sizes and button size of the cards around it, since those come from main-collection's settings that a section
 * rendered on a product URL cannot read. Infinite scroll (theme.js:6375) appends later pages without firing any
 * event, so a mutation observer removes the pinned product again if it arrives with one of them.
 *
 * Nothing in the theme reorders a grid: <product-list> only staggers the items it is given, and <product-facet>
 * re-renders #facet-main from the server, which is why <pinned-product> lives inside it and simply runs again.
 *
 * An optional &color=<value> (e.g. color=Kulhad+(Rust)) checks that color swatch on each pinned card and fires the
 * same change event a click would, so <product-item> (theme.js:6129) swaps the image, price, add to cart, wishlist
 * and links itself. A card without that color stays on its default color.
 */
(() => {
  class PinnedProduct extends HTMLElement {
    async connectedCallback() {
      const params = new URLSearchParams(window.location.search);

      this.handles = [...new Set((params.get('pin') || '').split(',').map((handle) => handle.trim().toLowerCase()).filter(Boolean))];
      this.color = params.get('color')?.trim().toLowerCase();
      this.listElement = document.querySelector(this.getAttribute('list-selector'));

      // A filter can exclude a pinned product, and a later page is somewhere the visitor scrolled or clicked to
      if (this.handles.length === 0 || !this.listElement || parseInt(params.get('page') || '1') > 1 || Array.from(params.keys()).some((key) => key.startsWith('filter.'))) {
        return;
      }

      const cards = (await Promise.all(this.handles.map(async (handle) => {
        const existing = this._findCards(this.listElement.children).find((node) => this._handleOf(node) === handle);

        if (existing) {
          return existing;
        }

        const card = await this._fetchCard(handle);
        card && this._matchGrid(card);

        return card;
      }))).filter(Boolean); // A handle that matches no product is skipped

      // Nothing to pin, or a filter re-render replaced the grid while the cards were loading
      if (cards.length === 0 || !this.isConnected || !this.listElement.isConnected) {
        return;
      }

      cards.forEach((card) => card.setAttribute('data-pinned', ''));
      this.listElement.prepend(...cards);
      cards.forEach((card) => this._selectColor(card));

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => this._findCards(mutation.addedNodes).forEach((item) => !cards.includes(item) && item.remove()));
      });

      this.observer.observe(this.listElement, { childList: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    // Matched on the title link's last path segment, so "dutch-oven" never matches "dutch-oven-26cm"
    _handleOf(node) {
      const link = node.matches?.('product-item') && node.querySelector('.product-item-meta__title');

      return link ? decodeURIComponent(new URL(link.href).pathname.split('/').pop()).toLowerCase() : null;
    }

    _findCards(nodes) {
      return Array.from(nodes).filter((node) => this.handles.includes(this._handleOf(node)));
    }

    async _fetchCard(handle) {
      const root = window.Shopify?.routes?.root || '/',
            url = `${root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.getAttribute('section'))}`;

      try {
        const response = await fetch(url);

        if (!response.ok) {
          return null;
        }

        return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('product-item');
      } catch (error) {
        console.warn('[pinned-product] leaving the collection as rendered:', error);
        return null;
      }
    }

    _selectColor(card) {
      const radio = this.color && Array.from(card.querySelectorAll('.product-item-meta__swatch-list .color-swatch__radio')).find((input) => input.value.trim().toLowerCase() === this.color);

      if (!radio || radio.checked) {
        return;
      }

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _matchGrid(card) {
      const reference = this.listElement.querySelector('product-item');

      if (!reference) {
        return;
      }

      const sizes = reference.querySelector('img[sizes]')?.getAttribute('sizes'),
            isSmall = reference.querySelector('.button--outline.button--small') !== null;

      if (sizes) {
        card.querySelectorAll('img[sizes]').forEach((image) => image.setAttribute('sizes', sizes));
      }

      card.querySelectorAll('.button--outline').forEach((button) => button.classList.toggle('button--small', isSmall));
    }
  }

  if (!window.customElements.get('pinned-product')) {
    window.customElements.define('pinned-product', PinnedProduct);
  }
})();
